/**
 * Web renderer adapter.
 *
 * `@plerity/toast` is a tier-1 (universal) package and may not import the tier-2
 * `@plerity/ui-core`, so the container component is injected. The ready-made,
 * pre-bound host is `ToastHost`, exported from ui-core's Toast entry point.
 *
 * ARIA roles (alert for error/warning, status for success/info) are owned by the
 * injected container's Toast atom; this adapter only maps `ToastItem` -> the
 * container's data shape and routes dismissals back to the store.
 */

import { type ComponentType, useCallback, useMemo } from "react";
import { useToastStore } from "./react";
import { toastStore } from "./toast";
import type { ToastAction, ToastItem, ToastStore, ToastVariant } from "./types";

/** Structural twin of ui-core's `ToastData`. */
export interface WebToastData {
  id: string;
  variant: ToastVariant;
  title: string;
  description?: string;
  /** Always 0 here — the store owns auto-dismiss timers; the atom must not run a second one. */
  duration?: number;
  dismissible?: boolean;
  action?: ToastAction;
}

/** Structural twin of ui-core's `ToastContainerProps`. */
export interface WebToastContainerProps {
  toasts: WebToastData[];
  onDismiss: (id: string) => void;
  position?: "top-right" | "bottom-right";
}

export interface WebToastHostProps {
  Container: ComponentType<WebToastContainerProps>;
  store?: ToastStore;
  position?: WebToastContainerProps["position"];
}

export function toWebToast(item: ToastItem): WebToastData {
  return {
    id: item.id,
    variant: item.variant,
    title: item.message,
    description: item.description,
    duration: 0,
    dismissible: item.dismissible,
    action: item.action,
  };
}

export function WebToastHost({ Container, store = toastStore, position }: WebToastHostProps) {
  const items = useToastStore(store);
  const toasts = useMemo(() => items.map(toWebToast), [items]);
  const onDismiss = useCallback((id: string) => store.dismiss(id), [store]);
  return <Container toasts={toasts} onDismiss={onDismiss} position={position} />;
}
